import type { QaProgressController, QaProgressNode, QaProgressSnapshot, QaRunPace, StepStatus } from './types';

let snapshot: QaProgressSnapshot = {
  open: true,
  running: false,
  scenarioId: null,
  scenarioTitle: null,
  pace: 'normal',
  rootNodes: [],
};

const listeners = new Set<() => void>();

export function subscribeQaProgress(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getQaProgressSnapshot(): QaProgressSnapshot {
  return snapshot;
}

/** סגירת הפופאף בלי לעצור ריצה (הריצה ממשיכה ברקע) */
export function dismissQaFloatingPanel(): void {
  setSnap({ ...snapshot, open: false });
}

function emit(): void {
  listeners.forEach((l) => l());
}

function setSnap(next: QaProgressSnapshot): void {
  snapshot = next;
  emit();
}

function cloneNode(n: QaProgressNode): QaProgressNode {
  return {
    ...n,
    children: (n.children ?? []).map(cloneNode),
  };
}

function mapNodes(
  nodes: QaProgressNode[],
  id: string,
  fn: (n: QaProgressNode) => QaProgressNode,
): QaProgressNode[] {
  return nodes.map((n) => {
    if (n.id === id) return fn(n);
    if (n.children?.length) return { ...n, children: mapNodes(n.children, id, fn) };
    return n;
  });
}

function addChildDeep(nodes: QaProgressNode[], parentId: string, child: QaProgressNode): QaProgressNode[] {
  return nodes.map((n) => {
    if (n.id === parentId) {
      return { ...n, children: [...(n.children ?? []), cloneNode(child)] };
    }
    if (n.children?.length) {
      return { ...n, children: addChildDeep(n.children, parentId, child) };
    }
    return n;
  });
}

export function createQaProgressController(opts: {
  scenarioId: string;
  scenarioTitle: string;
  pace: QaRunPace;
}): QaProgressController {
  const { scenarioId, scenarioTitle, pace } = opts;

  const ctl: QaProgressController = {
    startRun() {
      setSnap({
        ...snapshot,
        open: true,
        running: true,
        scenarioId,
        scenarioTitle,
        pace,
        rootNodes: [
          {
            id: '__boot',
            title: 'מתחילים…',
            status: 'running',
            children: [],
          },
        ],
      });
    },

    reset(rootNodes: QaProgressNode[]) {
      setSnap({
        ...snapshot,
        open: true,
        running: true,
        scenarioId,
        scenarioTitle,
        pace,
        rootNodes: rootNodes.map((n) => cloneNode(n)),
      });
    },

    start(id: string) {
      setSnap({
        ...snapshot,
        rootNodes: mapNodes(snapshot.rootNodes, id, (n) => ({ ...n, status: 'running' })),
      });
    },

    setHint(id: string, hint: string) {
      setSnap({
        ...snapshot,
        rootNodes: mapNodes(snapshot.rootNodes, id, (n) => ({ ...n, hint })),
      });
    },

    complete(id: string, status: StepStatus, hint?: string) {
      setSnap({
        ...snapshot,
        rootNodes: mapNodes(snapshot.rootNodes, id, (n) => ({
          ...n,
          status,
          hint: hint ?? n.hint,
        })),
      });
    },

    addNested(parentId: string, child: QaProgressNode) {
      setSnap({
        ...snapshot,
        rootNodes: addChildDeep(snapshot.rootNodes, parentId, child),
      });
    },

    endRun() {
      setSnap({
        ...snapshot,
        running: false,
      });
    },

    clear() {
      setSnap({
        open: snapshot.open,
        running: false,
        scenarioId: null,
        scenarioTitle: null,
        pace: snapshot.pace,
        rootNodes: [],
      });
    },

    setOpen(open: boolean) {
      setSnap({ ...snapshot, open });
    },
  };

  return ctl;
}
